import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { AlertCircle } from "lucide-react";
import ReactDOM from "react-dom";
import DocViewer, { DocViewerRenderers } from "react-doc-viewer";

interface CVFullScreenModalProps {
  cvUrl: string;
  name?: string;
  onClose: () => void;
}

export const CVFullScreenModal = ({ cvUrl, name, onClose }: CVFullScreenModalProps) => {
  const [hasError, setHasError] = useState(false);
  const [show, setShow] = useState(false);

  useEffect(() => {
    setHasError(false);
    setTimeout(() => setShow(true), 10);
  }, [cvUrl]);

  // Helper to get file extension
  const getExtension = (filename?: string) => {
    if (!filename) return '';
    const parts = filename.split('.');
    return parts.length > 1 ? parts.pop()?.toLowerCase() : '';
  };
  const ext = getExtension(name || cvUrl);
  const isDocx = ext === 'docx';

  // Modal content
  const modalContent = (
    <div className="fixed inset-0 z-50">
      {/* Overlay */}
      <div
        className="absolute inset-0 bg-black bg-opacity-80 transition-opacity duration-300"
        onClick={onClose}
      />
      {/* Centered modal */}
      <div
        className={`absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-white dark:bg-gray-800 rounded-sm shadow-lg overflow-hidden flex flex-col transition-transform duration-500 ease-in-out
          ${show ? 'scale-100 opacity-100' : 'scale-90 opacity-0'}
        `}
        style={{ width: '90vw', height: '90vh', maxWidth: 1200, maxHeight: '90vh' }}
        onClick={e => e.stopPropagation()}
      >
        <div className="flex justify-between items-center p-4 border-b">
          <h3 className="text-lg font-semibold">Document</h3>
          <Button variant="ghost" size="sm" onClick={onClose}>
            Close
          </Button>
        </div>
        <div className="flex-1 overflow-auto bg-gray-100 dark:bg-gray-800">
          {hasError ? (
            <div className="flex flex-col items-center justify-center h-full p-6 text-center">
              <AlertCircle className="h-12 w-12 text-red-500 mb-4" />
              <h3 className="text-lg font-semibold mb-2">Unable to load document</h3>
              <p className="text-gray-600 dark:text-gray-300 mb-4">
                The document could not be loaded. It may have been deleted or the URL is invalid.
              </p>
              <Button variant="outline" onClick={onClose}>
                Close
              </Button>
            </div>
          ) : (
            isDocx ? (
              <div className="w-full h-full min-h-[80vh] bg-white">
                <DocViewer
                  documents={[{ uri: cvUrl, fileType: ext }]}
                  pluginRenderers={DocViewerRenderers}
                  style={{ height: '80vh' }}
                  config={{ header: { disableHeader: true } }}
                />
              </div>
            ) : (
              <iframe
                src={cvUrl}
                className="w-full h-full min-h-[80vh] border-0 bg-white"
                title="Document"
                onError={() => setHasError(true)}
                allow="fullscreen"
              />
            )
          )}
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modalContent, document.body);
};
